import type { Locale, Quota } from "../types.ts";

/** Hạn mức có hai tầng — 20 câu/người/ngày và 300 câu/công ty/tháng. Core trả
 *  về tầng nào đang chạm trần trước, và `period` quyết định cách gọi tên nó. */
type Period = NonNullable<Quota["period"]>;

export interface Strings {
  title: string;
  subtitle: string;
  placeholder: string;
  send: string;
  stop: string;
  close: string;
  open: string;
  record: string;
  stopRecord: string;
  suggested: string;
  nextSteps: string;
  source: string;
  sampleData: string;
  progress: (stage: string) => string;
  retry: string;
  networkError: string;
  timeoutError: string;
  emptyError: string;
  authError: string;
  /** 403 `no_party_membership` (ami-agent-v1 1.1.0-draft, ADR-0001 §4): the signed-in person belongs to no party of this tenant. */
  noPartyMembership: string;
  /** 403 `active_party_mismatch`: the party the host app sent is not the one the person belongs to. */
  activePartyMismatch: string;
  quotaLeft: (remaining: number, period: Period) => string;
  quotaSpent: (resetAt: string, period: Period) => string;
  quotaSpentNoTime: (period: Period) => string;
  charLimit: (used: number, max: number) => string;
  newChat: string;
  speechDenied: string;
  waiting: string;
  listening: string;
  transcribing: string;
  speechFailed: string;
}

const EN: Strings = {
  title: "Apus Ami",
  subtitle: "Operations assistant",
  placeholder: "Ask about orders, BOM, processes…",
  send: "Send",
  stop: "Stop generating",
  close: "Close assistant",
  open: "Open assistant",
  record: "Record voice",
  stopRecord: "Stop recording",
  suggested: "Suggested",
  nextSteps: "Next steps",
  source: "Source",
  sampleData: "Sample data",
  progress: (stage) => {
    switch (stage) {
      case "received_request": return "Question received";
      case "resolve_context": return "Preparing relevant information";
      case "analyze_question": return "We are analyzing your question";
      case "stream_answer": return "Preparing your answer";
      default: return "Working on your request";
    }
  },
  retry: "Retry",
  networkError: "The assistant is temporarily unavailable. Please try again.",
  timeoutError: "The assistant took too long to answer. Please try again.",
  emptyError: "The assistant returned an empty answer.",
  authError: "Your session expired. Please refresh the page and try again.",
  noPartyMembership: "Your account is not a member of any party of this workspace. Ask an administrator to add you.",
  activePartyMismatch: "The party you are working in is not the one your account belongs to. Switch party and try again.",
  quotaLeft: (n, period) => n + (period === "month" ? " questions left this month" : " questions left today"),
  quotaSpent: (at, period) =>
    (period === "month" ? "You have used this month's questions. They reset on " : "You have used today's questions. They reset at ") +
    at + ".",
  quotaSpentNoTime: (period) =>
    period === "month" ? "You have used this month's questions." : "You have used today's questions.",
  charLimit: (used, max) => used + " / " + max,
  newChat: "New conversation",
  speechDenied: "Microphone permission was denied.",
  waiting: "Waiting for the assistant…",
  listening: "Listening… sends when you stop talking",
  transcribing: "Transcribing your voice…",
  speechFailed: "Could not transcribe — please try again or type.",
};

const VI: Strings = {
  title: "Apus Ami",
  subtitle: "Trợ lý vận hành",
  placeholder: "Hỏi về đơn hàng, BOM, quy trình…",
  send: "Gửi",
  stop: "Dừng trả lời",
  close: "Đóng trợ lý",
  open: "Mở trợ lý",
  record: "Ghi âm",
  stopRecord: "Dừng ghi âm",
  suggested: "Gợi ý",
  nextSteps: "Bước tiếp theo",
  source: "Nguồn",
  sampleData: "Số mẫu",
  progress: (stage) => {
    switch (stage) {
      case "received_request": return "Đã nhận câu hỏi";
      case "resolve_context": return "Đang chuẩn bị thông tin liên quan";
      case "analyze_question": return "Chúng tui đang phân tích câu hỏi của bạn";
      case "stream_answer": return "Đang tổng hợp câu trả lời";
      default: return "Đang xử lý yêu cầu của bạn";
    }
  },
  retry: "Thử lại",
  networkError: "Trợ lý tạm thời không sẵn sàng. Vui lòng thử lại.",
  timeoutError: "Trợ lý phản hồi quá lâu. Vui lòng thử lại.",
  emptyError: "Trợ lý không trả về nội dung.",
  authError: "Phiên đăng nhập đã hết hạn. Vui lòng tải lại trang rồi thử lại.",
  noPartyMembership: "Tài khoản của bạn không thuộc đơn vị (party) nào của không gian làm việc này. Hãy nhờ quản trị viên thêm bạn.",
  activePartyMismatch: "Đơn vị (party) đang làm việc không phải đơn vị tài khoản của bạn thuộc về. Hãy đổi đơn vị rồi thử lại.",
  quotaLeft: (n, period) => "Còn " + n + (period === "month" ? " câu tháng này" : " câu hôm nay"),
  quotaSpent: (at, period) =>
    "Bạn đã dùng hết số câu " + (period === "month" ? "tháng này" : "hôm nay") +
    ". Hạn mức đặt lại " + (period === "month" ? "ngày " : "lúc ") + at + ".",
  quotaSpentNoTime: (period) =>
    "Bạn đã dùng hết số câu " + (period === "month" ? "tháng này" : "hôm nay") + ".",
  charLimit: (used, max) => used + " / " + max,
  newChat: "Cuộc trò chuyện mới",
  speechDenied: "Trình duyệt đã từ chối quyền dùng micro.",
  waiting: "Đang chờ trợ lý…",
  listening: "Đang nghe… nói xong sẽ tự gửi",
  transcribing: "Đang nhận dạng giọng nói…",
  speechFailed: "Chưa nhận dạng được — thử nói lại hoặc gõ câu hỏi.",
};

export function strings(locale: Locale): Strings {
  return locale === "vi" ? VI : EN;
}

export function speechLang(locale: Locale): string {
  return locale === "vi" ? "vi-VN" : "en-US";
}

/**
 * Mốc reset, theo múi giờ của người xem.
 *
 * Hạn mức ngày reset trong hôm nay nên chỉ cần giờ; hạn mức tháng reset sang
 * một ngày khác nên phải có ngày — "đặt lại lúc 00:00" cho một trần tháng là
 * câu vô nghĩa. ISO hỏng thì trả nguyên văn, thà hiện chuỗi thô còn hơn hiện
 * "Invalid Date".
 */
export function formatResetTime(iso: string, locale: Locale, period: Period = "day"): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    const tag = locale === "vi" ? "vi-VN" : "en-US";
    const options: Intl.DateTimeFormatOptions =
      period === "month"
        ? { day: "2-digit", month: "2-digit" }
        : { hour: "2-digit", minute: "2-digit" };
    return new Intl.DateTimeFormat(tag, options).format(date);
  } catch {
    return iso;
  }
}
